import { Injectable } from '@angular/core';
import { AdministratorFacade } from '@piqet-angular/data';
import {
  dblclickTooltipDef,
  defaultColumn,
  DELETE_RECORD_LABEL,
  grid_widths_map,
  rolesModuleName,
  subModuleName,
} from '@piqet-angular/shared';
import { right } from '@popperjs/core';
import { ColDef } from 'ag-grid-community';
import { AdministratorService } from 'libs/data/src/lib/services/administrator/administrator.service';
import { getRolesAndPermission } from 'libs/shared/src/lib/service/helper.service';
import { Subject } from 'rxjs';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';

@Injectable({
  providedIn: 'root',
})
export class RecycledContentDataGridService {
  event: any;
  recycledPayload: any;
  editRecycledContentData = new Subject<[any, any, any]>();
  recycledContentEditObservable$ = this.editRecycledContentData.asObservable();
  deleteRecycledContentData = new Subject<[any, any, any]>();
  recycledContentDeleteObservable$ = this.deleteRecycledContentData.asObservable();

  constructor(
    private swalService: SwalService,
    private administratorFacade: AdministratorFacade,
    private administratorService: AdministratorService
  ) {}

  getRecycledContentColumn(): ColDef[] {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'OrderId',
      headerName: 'OrderId',
      field: 'OrderId',
      hide: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'MaterialName',
      headerName: 'Material category',
      field: 'materialName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      filter: 'agTextColumnFilter',
      lockVisible: true,
      wrapHeaderText: true,
      cellStyle: { 'text-decoration': 'underline' },
      cellRenderer: (params: any) => {
        const cellValue = params.value;
        const cellElement = document.createElement('span');
        cellElement.style.cursor = 'pointer';
        cellElement.innerHTML = `${cellValue}`;
        cellElement.title = dblclickTooltipDef;
        cellElement.addEventListener('dblclick', () => {
          const checkAccess = this.isAccess() ? this.isAccess().isEdit : true;
          checkAccess &&
            this.editRecycledContentData.next([
              params.data.materialId,
              params.data.regionId,
              'Review recycled content detail',
            ]);
        });
        return cellElement;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'MaterialCode',
      headerName: 'Material code',
      field: 'materialCode',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      filter: 'agTextColumnFilter',
      lockVisible: true,
      wrapHeaderText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'MaterialName',
      headerName: 'Material',
      field: 'materialName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      filter: 'agTextColumnFilter',
      lockVisible: true,
      cellRenderer: (params: any) => {
        return `<div class="truncated-text" title="${params.value}">${
          params.value ? params.value : ''
        }</div>`;
      },
      wrapHeaderText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'RegionCode',
      headerName: 'Region code',
      field: 'regionCode',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      filter: 'agTextColumnFilter',
      lockVisible: true,
      wrapHeaderText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'RegionName',
      headerName: 'Region',
      field: 'regionName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      filter: 'agTextColumnFilter',
      lockVisible: true,
      wrapHeaderText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'OrganizationName',
      headerName: 'Organization',
      field: 'organizationName',
      minWidth: grid_widths_map.sm_column_sm,
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      filter: 'agTextColumnFilter',
      lockVisible: true,
      wrapHeaderText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      maxWidth: grid_widths_map.sm_column_xs,
      cellRendererParams: {
        showEdit: this.isAccess() ? this.isAccess().isEdit : true,
        showDelete: this.isAccess() ? this.isAccess().isDelete : true,
        value: 'adminMaterial',
        editData: (data: any, pathName: any) => {
          this.editRecycledContentData.next([
            data.materialId,
            data.regionId,
            'Review recycled content detail',
          ]);
        },
        deleteData: (data: any, pathName: any) => {
          this.deleteProjectWorkflowConfirmation(data.materialId, data.regionId, pathName);
        },
      },
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'actionRenderer',
      lockVisible: true,
      lockPosition: right,
      resizable: false,
    });

    return colDef;
  }

  isAccess = () => {
    return getRolesAndPermission(
      rolesModuleName.DataManageRecycledContent,
      subModuleName.EOLDataModule
    );
  };
  deleteProjectWorkflowConfirmation(materialId: any, regionId: any, pathName: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.deleteRecycledContentData.next([materialId, regionId, pathName]);
      }
    });
  }

  getPayload(searchData: any, sortColumnState?: any, event?: any) {
    if (event) {
      this.event = event;
    }
    const currentPage = this.event?.goToPage
      ? this.event.goToPage
      : this.event?.next
      ? this.event.currentPage + 1
      : this.event?.prev
      ? this.event?.currentPage - 1
      : this.event?.gridApi?.paginationGetCurrentPage() + 1;
    const pageSize = this.event?.gridApi.paginationGetPageSize();
    const startCount = pageSize * (currentPage - 1);

    const columns = this.getRecycledContentColumn()
      .map((item: any) => {
        return {
          searchable: true,
          name: item.colId,
          orderable: true,
          data: item.colId,
        };
      })
      .filter(value => value?.name != 'Actions');

    return (this.recycledPayload = {
      search: {
        columnNameList: [],
        value: searchData || '',
      },
      columns: columns,
      order: [
        {
          columnName: sortColumnState?.columnName || 'OrderId',
          dir:
            sortColumnState?.dir[0].toUpperCase() + sortColumnState?.dir.substr(1).toLowerCase() ||
            'Asc',
        },
      ],
      start: startCount || 0,
      length: pageSize == Number(this.event?.totalRecord) ? Number('-1') : pageSize || 10,
      draw: currentPage || 1,
    });
  }

  alertConfirmation(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.administratorService
          .deleteRecycledContent(data.materialId, data.regionId)
          .subscribe((res: any) => {
            this.administratorFacade.deleteRecycledContentSuccess(res);
          });
      }
    });
  }
}
